"use client";

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/axios";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

type Voucher = {
  id: number;
  code: string;
  amount: string;
  balance: string;
  valid_from: string;
  valid_until: string;
  usage_count: number;
  max_usage: number;
};

export default function MarketingVouchersPage() {
  const { data, refetch, isLoading } = useQuery({
    queryKey: ["vouchers"],
    queryFn: async () => {
      const { data: d } = await api.get<{ vouchers: Voucher[] }>("/api/vouchers");
      return d.vouchers;
    },
  });

  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState("");
  const [until, setUntil] = useState("");

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/marketing"
          className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-2 inline-flex")}
        >
          <ArrowLeft className="h-4 w-4" />
          Marketing
        </Link>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger>
            <Button>New voucher</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create voucher</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid gap-2">
                <Label>Amount (₹)</Label>
                <Input value={amt} onChange={(e) => setAmt(e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="voucher-valid-until">Valid until</Label>
                <Input
                  id="voucher-valid-until"
                  type="date"
                  value={until}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setUntil(e.target.value)}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                onClick={async () => {
                  const n = Number(amt);
                  if (!Number.isFinite(n) || n <= 0) {
                    return;
                  }
                  await api.post("/api/vouchers", {
                    amount: n,
                    valid_until: until.trim() || undefined,
                  });
                  setAmt("");
                  setUntil("");
                  setOpen(false);
                  void refetch();
                }}
              >
                Create
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <h1 className="text-2xl font-semibold tracking-tight">Gift vouchers</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Inventory</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="p-3 font-medium">Code</th>
                    <th className="p-3 font-medium text-right">Balance</th>
                    <th className="p-3 font-medium">Valid</th>
                    <th className="p-3 font-medium text-right">Uses</th>
                  </tr>
                </thead>
                <tbody>
                  {(data ?? []).map((v) => (
                    <tr key={v.id} className="border-t">
                      <td className="p-3 font-mono font-medium">{v.code}</td>
                      <td className="p-3 text-right tabular-nums">₹{v.balance}</td>
                      <td className="p-3 text-muted-foreground text-xs whitespace-nowrap">
                        {v.valid_from} → {v.valid_until}
                      </td>
                      <td className="p-3 text-right tabular-nums">
                        {v.usage_count}/{v.max_usage}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
